import { useState } from 'react';
import { ReportAlreadyScheduledError } from 'simInventory/SimInventoryApi/simInventoryApi';
import { Trans, useTranslation } from 'react-i18next';
import { AuthError } from 'auth/utils';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { rsimFiltersAtom, rsimSortingAtomWithStorage } from 'atoms/filters/rsim-products';
import {
  checkIsLargeReport,
  downloadRSimProductsReportWithBackgroundSupport,
  MaxRSimProductsCountError,
} from '../RSimApi/rSimReportsApi';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LargeExportPrompt } from 'common/components/Export/LargeExportPrompt';
import { ExportCardsSnackbars, ExportError } from 'common/components/Export/ExportCardsSnackbars';
import { pxToRem } from 'src/common/utils/fonts';

import { useDebounce } from 'src/common/useDebounce';
import {
  GoogleTagManagerActions,
  sendGoogleTagManagerAction,
} from 'src/common/utils/sendGoogleTagManagerAction';

interface RSimDownloadLargeReportButtonProps {
  totalItems: number;
  disabled?: boolean;
}

export const RSimDownloadLargeReportButton = ({
  totalItems,
  disabled,
}: RSimDownloadLargeReportButtonProps) => {
  const { t } = useTranslation();
  const filters = useAtomValue(rsimFiltersAtom);
  const { tags, searchText, chosenIdType, ...otherFilters } = filters;
  const sorting = useAtomValue(rsimSortingAtomWithStorage);

  const [downloadingSnackbarOpened, setDownloadingSnackbarOpened] = useState(false);
  const [exportError, setExportError] = useState<ExportError | null>(null);
  const [confirmationRequire, setConfirmationRequire] = useState(false);
  const [largeExport, setLargeExport] = useState(false);
  const [isLargeExportLoading, setIsLargeExportLoading] = useState(false);

  const debouncedSearchText = useDebounce(searchText || '', 500);
  const minLengthText = debouncedSearchText.length < 3 ? '' : debouncedSearchText;

  const { mutate, isPending } = useMutation({
    mutationKey: [
      'rsim-products',
      'reports',
      'download',
      {
        searchText: minLengthText,
        chosenIdType,
        tags,
        ...otherFilters,
      },
      sorting,
    ],
    mutationFn: async () => {
      await downloadRSimProductsReportWithBackgroundSupport(
        {
          searchText: minLengthText,
          chosenIdType,
          tags,
          ...otherFilters,
        },
        sorting,
      );
    },
    retry: 0,
    onError: (error) => {
      if (error instanceof AuthError) {
        throw error;
      }

      if (error instanceof MaxRSimProductsCountError) {
        setExportError({
          title: t('common.exportFailed'),
          description: [
            t('products.rSim.maxRSimProductsCount', {
              maxRSims: error.maxRSimProductsCount,
            }),
          ],
        });
      } else if (error instanceof ReportAlreadyScheduledError) {
        setExportError({
          title: t('common.exportFailed'),
          description: [
            t('simInventory.anotherBackgroundExportIsStillInProgress'),
            t('simInventory.pleaseTryAgainLater'),
          ],
        });
      } else {
        setExportError({
          title: t('common.requestFailed'),
          description: [t('common.somethingWrong')],
        });
      }

      sendGoogleTagManagerAction(
        largeExport
          ? GoogleTagManagerActions.LargeRSimProductExportFailure
          : GoogleTagManagerActions.SmallRSimProductExportFailure,
      );
    },
    onSuccess: () => {
      setDownloadingSnackbarOpened(true);
      sendGoogleTagManagerAction(
        largeExport
          ? GoogleTagManagerActions.LargeRSimProductExportSuccess
          : GoogleTagManagerActions.SmallRSimProductExportSuccess,
      );
    },
  });

  const exportReport = async () => {
    try {
      setIsLargeExportLoading(true);
      const largeExport = await checkIsLargeReport({
        searchText: minLengthText,
        chosenIdType,
        tags,
        ...otherFilters,
      });

      setLargeExport(largeExport);
      setConfirmationRequire(largeExport);
      setIsLargeExportLoading(false);

      if (!largeExport) {
        mutate();
      }
    } catch {
      setIsLargeExportLoading(false);
      setExportError({
        title: t('common.requestFailed'),
        description: [t('common.somethingWrong')],
      });
    }
  };

  const exportDisabled = disabled || isPending || totalItems === 0 || isLargeExportLoading;

  return (
    <Stack>
      <Button
        startIcon={<FileDownloadIcon sx={{ width: pxToRem(16), height: pxToRem(16) }} />}
        disabled={exportDisabled}
        color="secondary"
        onClick={exportReport}
      >
        {t('common.export')}
      </Button>
      <LargeExportPrompt
        open={confirmationRequire}
        onConfirm={async () => {
          mutate();
          setConfirmationRequire(false);
        }}
        onClose={() => {
          setConfirmationRequire(false);
          setLargeExport(false);
        }}
        title={t('products.rSim.rSimExport')}
        message={
          <>
            <Trans
              i18nKey={'products.rSim.requestOfNRSimsExport'}
              values={{ numberOfCards: totalItems || 0 }}
              components={{ 1: <strong /> }}
            />
            <div>{t('common.emailWithDownloadLink')}</div>
          </>
        }
      />
      <ExportCardsSnackbars
        opened={downloadingSnackbarOpened}
        largeExportSuccessTitle={t('products.rSim.exportingNRSims', {
          numberOfCards: totalItems || 0,
        })}
        largeExport={largeExport}
        setExportError={setExportError}
        setDownloadingSnackbarOpened={setDownloadingSnackbarOpened}
        setLargeExport={setLargeExport}
        exportError={exportError}
      />
    </Stack>
  );
};
